import React, { useEffect, useMemo, } from 'react';
import { Button } from 'reactstrap';
import { isEmpty, pick, omit, keyBy, sortBy, } from 'lodash';
import { setDoc, updateDoc, query, collection, doc, } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';
import qs from 'qs';
import { Link } from 'react-router-dom';
import { Container, Draggable } from 'react-smooth-dnd';
import { arrayMoveImmutable as arrayMove } from 'array-move';
import { useToggle } from 'react-use';
import { format as formatDate } from 'date-fns';

import { functions, } from '../../firebase';
import env from '../../env';
import createComputeSalesJob, { startRakutenSyncAndBackfill, startAmazonSyncAndBackfill, BACKFILL_DAYS, startShopifySyncAndBackfill, startYahooSyncAndBackfill } from '../../createComputeSalesJob';
import { fields as sellerFields, } from '../../shared/models/seller';
import { fields as shopFields, mallOptions, rppCredentialFieldNames, shopifyCredentialFieldNames, yahooCredentialFieldNames, canUseShopify as canUseShopifyFor, } from '../../shared/models/shop';
import { malls } from '../../shared/models/feeMalls';
import useQueryParams from '../hooks/useQueryParams';
import useUser from '../hooks/useUser';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import ModelFormModal from '../modals/ModelFormModal';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import AddButton from '../AddButton';
import TenantPage from '../TenantPage';
import useTenant from '../hooks/useTenant';
import SettingsTabs from '../SettingsTabs';
import RakutenFeeNote from '../RakutenFeeNote';
import DataSyncDelayNote from '../DataSyncDelayNote';

const saveRakutenRppCredentials = httpsCallable(functions, 'saveRakutenRppCredentials');
const testRakutenRppConnection = httpsCallable(functions, 'testRakutenRppConnection');
const saveYahooCredentials = httpsCallable(functions, 'saveYahooCredentials');
const testYahooApiConnection = httpsCallable(functions, 'testYahooApiConnection');
const startHistoricalBackfill = httpsCallable(functions, 'startHistoricalBackfill');

const mallLabels = Object.fromEntries(mallOptions.map(_ => [_.value, _.label]));

const processShopValues = (values) => ({
  // RPP 認証情報は平文を shop に残さず onCall で暗号化保存するため除去する。
  // Shopify の連携情報は OAuth コールバックが書くため、hidden フィールドの null で
  // 上書きしてしまわないよう同様に除去する（消すと連携が切れて売上が止まる）。
  // Yahoo も同様（Client Secret・公開鍵は onCall で暗号化保存、認証情報は OAuth が書く）
  ...omit(values, [...rppCredentialFieldNames, ...shopifyCredentialFieldNames, ...yahooCredentialFieldNames]),
  ...(values.rakutenShopUrl && {
    rakutenShopUrl: values.rakutenShopUrl.replace(/^https?:\/\/.*rakuten\.co\.jp\//, '').replace(/\//g, ''),
  }),
});

// 編集フォーム内の楽天向けヘッダー（手数料の注記 + RPP接続テスト）
// パスワードは password 型のため保存後はフォーム state に残らない（value が null）。
// そのまま送るとログイン情報が不完全になるので、平文4項目が揃っているときだけ平文でテストし、
// 揃っていない（＝保存済みの再テスト）ときは tenantId/shopId を送り、サーバ側で復号して使う。
function RakutenRppConnectionTest({ statedFields, tenantId, shopId }) {
  const [isTesting, toggleTesting] = useToggle(false);
  const plainCredentials = {
    rLoginId: statedFields.rppRLoginId?.value,
    rLoginPassword: statedFields.rppRLoginPassword?.value,
    memberId: statedFields.rppMemberId?.value,
    memberPassword: statedFields.rppMemberPassword?.value,
  };
  // 平文が4項目すべて入力されているときだけ平文でテスト（新規入力 or 全項目更新時）
  const hasFullPlainInput = Object.values(plainCredentials).every(Boolean);
  // 平文が揃っていなくても、保存済み（shopId あり）なら復号テスト可能
  const canTest = hasFullPlainInput || (tenantId != null && shopId != null);
  const onTest = async () => {
    toggleTesting(true);
    try {
      const { data } = await testRakutenRppConnection(
        hasFullPlainInput ? plainCredentials : { tenantId, shopId },
      );
      if (data.success) {
        toast.success('接続テストに成功しました');
      } else {
        toast.error(data.message || '接続テストに失敗しました');
      }
    } catch (e) {
      console.error(e);
      toast.error('接続テストでエラーが発生しました');
    }
    toggleTesting(false);
  };
  return (
    <div className="mb-2">
      <Button color="link" className="p-0" onClick={onTest} disabled={isTesting || !canTest}>
        {isTesting ? '広告ログインを接続テスト中...' : '広告ログインの接続テスト'}
      </Button>
    </div>
  );
}

// Yahoo の接続テスト。認証情報は yahooSecrets にあるためサーバ側で復号して使う。
// 認証(OAuth)が済んでいないと必ず失敗するので、保存済みショップでのみ押せるようにする。
function YahooConnectionTest({ tenantId, shopId }) {
  const [isTesting, toggleTesting] = useToggle(false);
  const onTest = async () => {
    toggleTesting(true);
    try {
      const { data } = await testYahooApiConnection({ tenantId, shopId });
      if (data.success) {
        toast.success('接続テストに成功しました');
      } else {
        toast.error(data.message || '接続テストに失敗しました');
      }
    } catch (e) {
      console.error(e);
      toast.error('接続テストでエラーが発生しました');
    }
    toggleTesting(false);
  };
  return (
    <div className="mb-2">
      <Button color="link" className="p-0" onClick={onTest} disabled={isTesting || tenantId == null || shopId == null}>
        {isTesting ? 'Yahoo APIを接続テスト中...' : 'Yahoo APIの接続テスト'}
      </Button>
    </div>
  );
}

function HistoricalBackfillButton({ tenant, item }) {
  const [isStarting, toggleStarting] = useToggle(false);
  const requestedAt = item.historicalBackfillRequestedAt?.toDate();
  const onClick = async () => {
    const message = [
      `「${item.name}」の過去2年分の売上を取得します。`,
      '有料オプション（1セラーあたり20,000円・税抜）です。広告は対象外です。',
      ...(requestedAt ? [`${formatDate(requestedAt, 'yyyy/MM/dd HH:mm')} に実行済みです。再実行しますか？`] : ['よろしいですか？']),
    ].join('\n');
    if (!window.confirm(message)) return;
    toggleStarting(true);
    try {
      await startHistoricalBackfill({ tenantId: tenant.id, sellerId: item.id });
      toast.success('過去2年分の取得を開始しました。順次反映されます');
    } catch (e) {
      console.error(e);
      toast.error('過去2年分の取得の開始に失敗しました');
    }
    toggleStarting(false);
  };
  return (
    <Button color="warning" outline size="sm" onClick={onClick} disabled={isStarting} title={requestedAt && `${formatDate(requestedAt, 'yyyy/MM/dd HH:mm')} 実行済み`}>
      {requestedAt && <span className="fas fa-check me-1" />}過去2年取得
    </Button>
  );
}

// Yahoo の設定手順。どこで何を発行するのか分からないと詰まるため、フォーム上部に出す。
function YahooSetupNote() {
  return (
    <div className="alert alert-info small mb-2">
      <div className="fw-bold mb-1">Yahoo!ショッピング連携の準備</div>
      <ol className="mb-1 ps-3">
        <li>Yahoo!デベロッパーネットワークでアプリケーションを登録し、Client ID / Client Secret を発行</li>
        <li>「ショッピングAPI利用申請フォーム」から、Client ID とストアアカウントを指定して利用申請</li>
        <li>ストアクリエイターPro &gt; 設定 &gt; 暗号鍵管理 で公開鍵を発行（バージョンを控える）</li>
        <li>上記を入力して保存したあと、一覧の「Yahooと連携」から認証</li>
      </ol>
      <div className="text-muted">公開鍵の有効期限は発行から1年です。期限が近づくとメールでお知らせします。</div>
    </div>
  );
}

// renderFormHeader は { statedFields } しか受け取らないため、保存済みショップの
// tenantId/shopId はクロージャで束縛して渡す（新規追加時は shopId なし＝平文テストのみ）。
const makeRenderShopFormHeader = ({ tenantId, shopId } = {}) => ({ statedFields }) => {
  const mall = statedFields.mall?.value;
  if (mall === 'yahoo') {
    return (
      <>
        <YahooSetupNote />
        {shopId != null && <YahooConnectionTest tenantId={tenantId} shopId={shopId} />}
      </>
    );
  }
  if (mall !== 'rakuten') return null;
  return (
    <>
      <RakutenFeeNote />
      <RakutenRppConnectionTest statedFields={statedFields} tenantId={tenantId} shopId={shopId} />
    </>
  );
};

// 編集フォーム送信時に、入力された Yahoo の秘密情報を onCall で暗号化保存する。
// Client Secret / 公開鍵は空なら「変更なし」として既存維持される（バックエンド側で対応済み）。
const saveYahooCredentialsOnSubmit = (tenant, shopId) => async (values) => {
  const { yahooClientSecret, yahooPublicKey, yahooPublicKeyVersion, yahooPublicKeyExpiresAt } = values;
  // どれも入力されていなければ何もしない（通常の店舗情報編集のみ）
  if (![yahooClientSecret, yahooPublicKey, yahooPublicKeyVersion, yahooPublicKeyExpiresAt].some(Boolean)) return true;
  try {
    await saveYahooCredentials({
      tenantId: tenant.id,
      shopId,
      clientSecret: yahooClientSecret,
      publicKey: yahooPublicKey,
      publicKeyVersion: yahooPublicKeyVersion,
      publicKeyExpiresAt: yahooPublicKeyExpiresAt ? new Date(yahooPublicKeyExpiresAt).toISOString() : null,
    });
    toast.success('Yahooの認証情報を保存しました');
    return true;
  } catch (e) {
    console.error(e);
    toast.error(e.message || 'Yahooの認証情報の保存に失敗しました');
    return false;
  }
};

// 編集フォーム送信時に、入力された RPP 認証情報を onCall で暗号化保存する。
// EditButton の validateOnSubmit フック（生の値を受け取り、true で送信続行）を利用。
// パスワードは空なら「変更なし」として既存維持される（バックエンド側で対応済み）。
const saveRppCredentialsOnSubmit = (tenant, shopId) => async (values) => {
  const { rppRLoginId, rppRLoginPassword, rppMemberId, rppMemberPassword } = values;
  // 認証情報がどれも入力されていなければ何もしない（通常の店舗情報編集のみ）
  if (![rppRLoginId, rppRLoginPassword, rppMemberId, rppMemberPassword].some(Boolean)) return true;
  try {
    await saveRakutenRppCredentials({
      tenantId: tenant.id,
      shopId,
      rLoginId: rppRLoginId,
      rLoginPassword: rppRLoginPassword,
      memberId: rppMemberId,
      memberPassword: rppMemberPassword,
    });
    toast.success('楽天広告のログイン情報を保存しました');
    return true;
  } catch (e) {
    console.error(e);
    toast.error('楽天広告のログイン情報の保存に失敗しました');
    return false;
  }
};

export default function ShopIntegrations (props) {
  return (
    <TenantPage>
      <PageContent {...props} />
    </TenantPage>
  );
};

function PageContent (props) {
  const { tenant, history, } = props;
  const queryParams = useQueryParams();
  const { user } = useUser();
  // Shopify は審査・検証中のため、運営者と審査担当者用アカウントにのみ表示する
  const canUseShopify = canUseShopifyFor(user);
  const sellers = useCollectionSubscription(query(collection(tenant.ref, 'sellers')));
  const shops = useCollectionSubscription(query(collection(tenant.ref, 'shops')));
  const adApiAccounts = useCollectionSubscription(query(collection(tenant.ref, 'adApiAccounts')));
  const adApiAccountsById = keyBy(adApiAccounts, 'id');

  useEffect(() => {
    if(queryParams.amazon_callback_uri) {
      window.location.href = queryParams.amazon_callback_uri + '?' + qs.stringify({ redirect_uri: env('SP_API_AUTH_REDIRECT_URI'), amazon_state: queryParams.amazon_state, version: queryParams.version, });
    }
  }, [queryParams.amazon_callback_uri]);

  useEffect(() => {
    if(queryParams.accessToken && queryParams.sellerId) {
      (async () => {
        await setDoc(doc(collection(tenant.ref, 'sellers'), queryParams.sellerId), {
          sellerId: queryParams.sellerId,
          spApi: {
            ...pick(queryParams, ['accessToken', 'refreshToken', 'expires']),
          },
          authorizedAt: new Date(),
        }, { merge: true });
        try {
          // 直近分は即時取り込み。過去分(BACKFILL_DAYS)は「裏で商品取得 → 完了後に backfill」。
          // 商品マスタが揃う前に売上計算すると商品別売上が空になるため、商品同期の完了を待つ。
          await createComputeSalesJob(tenant);
          await startAmazonSyncAndBackfill(tenant, queryParams.sellerId);
        } catch (e) {
          // 取り込みに失敗しても定期実行でカバーされるため、連携完了は妨げない
          console.error(e);
        }
        toast.success(`Amazonセラーを連携しました。商品と売上データの取り込みを開始しました（過去${BACKFILL_DAYS}日分は順次反映されます）`);
        history.replace('/settings/shopIntegrations');
      })();
    }
  }, [queryParams.accessToken]);

  const items = useMemo(() => {
    const sellerItems = sellers.map(_ => ({ ..._, type: 'seller', channel: 'Amazon' }));
    const shopItems = shops.map(_ => ({ ..._, type: 'shop', channel: mallLabels[_.mall] || _.mall }));
    return sortBy([...sellerItems, ...shopItems], _ => _.index ?? Infinity);
  }, [sellers, shops]);

  const onDrop = async ({ removedIndex, addedIndex }) => {
    const newItems = arrayMove(items, removedIndex, addedIndex);
    await Promise.all(
      newItems.map((item, index) => {
        if (item.index === index) return;
        const collectionName = item.type === 'seller' ? 'sellers' : 'shops';
        return updateDoc(doc(collection(tenant.ref, collectionName), item.id), { index });
      })
    );
  };

  return (
    <div className="container-fluid">
      <div className="px-4">
        <div className="card p-4">
          <SettingsTabs activeTab="shopIntegrations" />
          {tenant.hasDemo ? (
            <div className="alert alert-info mb-3">デモ中はこの設定を変更できません。トライアル開始後に設定できます。</div>
          ) : (
            <>
              <DataSyncDelayNote />
              {/*
                Shopify 側から先にインストールされ、totono に対応する店舗が無かった場合、
                authShopifyCallback が ?shopifyShop= を付けて戻してくる。
                ここで店舗を作れば onWriteShop が保留中のトークンを引き当てて連携が完了する。
              */}
              {canUseShopify && [queryParams.shopifyShop]
                .filter(domain => domain && !shops.some(_ => _.shopifyShop === domain)).map(domain => (
                <div key={domain} className="alert alert-info d-flex align-items-center justify-content-between gap-3">
                  <div>
                    Shopifyストア「{domain}」のインストールを受け付けました。
                    店舗を追加すると連携が完了します。
                  </div>
                  <AddButton
                    label="この店舗を追加"
                    color="primary"
                    itemRef={_ => doc(collection(tenant.ref, 'shops'))}
                    FormModal={ModelFormModal}
                    formProps={{ title: 'Shopifyストアの追加', fields: shopFields({ user }), }}
                    initialValues={{ mall: 'shopify', shopifyShop: domain, name: domain.replace('.myshopify.com', '') }}
                    processValues={processShopValues}
                    onFinish={async () => {
                      try {
                        await createComputeSalesJob(tenant);
                      } catch (e) {
                        console.error(e);
                      }
                    }}
                  />
                </div>
              ))}
              <div className="mb-3 d-flex justify-content-start align-items-end gap-1">
                <Button tag="a" href={env('SP_API_AUTH_CONSENT_URI')} color="primary" outline>
                  SP API認証
                </Button>
                <AddButton
                  label="店舗追加"
                  itemRef={_ => doc(collection(tenant.ref, 'shops'))}
                  FormModal={ModelFormModal}
                  formProps={{ title: '店舗追加', fields: shopFields({ user }), renderFormHeader: makeRenderShopFormHeader(), }}
                  initialValues={{}}
                  processValues={processShopValues}
                  onFinish={async ({ id: shopId, mall }) => {
                    try {
                      // 直近分は即時。過去分は「裏で商品取得 → 完了後に売上 backfill」。
                      // 売上計上は商品マスタを参照するため商品同期の完了を待つ。
                      await createComputeSalesJob(tenant);
                      if (mall === 'shopify' || mall === 'yahoo') {
                        // Shopify / Yahoo は OAuth 完了後でないと商品を取得できない。
                        // 同期は連携完了後に一覧のボタンから開始する。
                        return;
                      }
                      await startRakutenSyncAndBackfill(tenant, shopId);
                    } catch (e) {
                      console.error(e);
                    }
                  }}
                />
              </div>
            </>
          )}
          <Container onDrop={onDrop} lockAxis="y" dragHandleSelector=".drag-handle">
            {
              items.map((item) => {
                const { id, ref, type, channel, name, sellerId, adApiAccountId, mall, } = item;
                const adApiAccount = adApiAccountsById[adApiAccountId];
                const isSellerType = type === 'seller';
                const isRakutenShop = type === 'shop' && mall === 'rakuten';
                const isShopifyShop = type === 'shop' && mall === 'shopify';
                const isYahooShop = type === 'shop' && mall === 'yahoo';

                return (
                  <Draggable key={id}>
                    <div className="border rounded p-3 mb-2 d-flex align-items-center gap-3 bg-white">
                      <span className="drag-handle cursor-pointer text-muted" style={{ cursor: 'grab' }}>
                        <span className="fas fa-grip-vertical" />
                      </span>
                      <span className={`badge ${(isSellerType ? malls.amazon : malls[mall])?.className || 'bg-secondary'}`}>
                        {channel}
                      </span>
                      <span className="fw-bold">{name}</span>
                      {isSellerType && sellerId && (
                        <span className="text-muted small">ID: {sellerId}</span>
                      )}
                      {isShopifyShop && (
                        item.shopifyAuthorizedAt ? (
                          <span className="text-muted small">
                            <span className="fas fa-check-circle text-success me-1" />連携済み
                          </span>
                        ) : (
                          <span className="text-danger small">
                            <span className="fas fa-exclamation-triangle me-1" />アプリ未インストール
                          </span>
                        )
                      )}
                      {isYahooShop && (
                        item.yahooAuthorizedAt ? (
                          <span className="text-muted small">
                            <span className="fas fa-check-circle text-success me-1" />連携済み
                          </span>
                        ) : (
                          <span className="text-danger small">
                            <span className="fas fa-exclamation-triangle me-1" />未連携
                          </span>
                        )
                      )}
                      {isSellerType && (
                        adApiAccount ? (
                          <span className="text-muted small">広告: {adApiAccount.name}</span>
                        ) : (
                          <span className="text-danger small">
                            <span className="fas fa-exclamation-triangle me-1" />広告APIアカウント未連携
                          </span>
                        )
                      )}
                      {!tenant.hasDemo && (
                        <div className="ms-auto d-flex align-items-center gap-1">
                          {user?.admin && isSellerType && (
                            <HistoricalBackfillButton tenant={tenant} item={item} />
                          )}
                          {isSellerType && (
                            <Button
                              tag="a"
                              href={env('SP_API_AUTH_CONSENT_URI')}
                              color="link"
                              className="p-1"
                              title="再連携(権限の更新)"
                            >
                              <span className="fas fa-rotate" />
                            </Button>
                          )}
                          {isShopifyShop && (
                            item.shopifyAuthorizedAt ? (
                              <Button
                                color="link"
                                className="p-1"
                                title="商品を取得して過去分を再集計"
                                onClick={() => startShopifySyncAndBackfill(tenant, id)}
                              >
                                <span className="fas fa-rotate" />
                              </Button>
                            ) : (
                              <Button
                                tag="a"
                                href={`${env('CLOUD_FUNCTIONS_ENDPOINT')}/authShopify?tenantId=${tenant.id}&shopId=${id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                color="primary"
                                outline
                                size="sm"
                              >
                                アプリをインストール
                              </Button>
                            )
                          )}
                          {isYahooShop && (
                            item.yahooAuthorizedAt ? (
                              <Button
                                color="link"
                                className="p-1"
                                title="商品を取得して過去分を再集計"
                                onClick={() => startYahooSyncAndBackfill(tenant, id)}
                              >
                                <span className="fas fa-rotate" />
                              </Button>
                            ) : (
                              <Button
                                tag="a"
                                href={`${env('CLOUD_FUNCTIONS_ENDPOINT')}/authYahoo?tenantId=${tenant.id}&shopId=${id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                color="primary"
                                outline
                                size="sm"
                              >
                                Yahooと連携
                              </Button>
                            )
                          )}
                          <EditButton
                            itemRef={ref}
                            FormModal={ModelFormModal}
                            formProps={{
                              title: isSellerType ? 'セラー編集' : '店舗編集',
                              fields: isSellerType ? sellerFields() : shopFields({ user }),
                              ...(!isSellerType && { renderFormHeader: makeRenderShopFormHeader({ tenantId: tenant.id, shopId: id }) }),
                            }}
                            {...(!isSellerType && { processValues: processShopValues })}
                            {...(isRakutenShop && {
                              validateOnSubmit: saveRppCredentialsOnSubmit(tenant, id),
                            })}
                            {...(isYahooShop && {
                              validateOnSubmit: saveYahooCredentialsOnSubmit(tenant, id),
                            })}
                          />
                          <DeleteButton itemRef={ref} />
                        </div>
                      )}
                    </div>
                  </Draggable>
                );
              })
            }
          </Container>
          {items.length === 0 && (
            <div className="text-muted text-center py-4">
              ショップが登録されていません
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
